import {
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CFormInput,
  CInputGroup,
  CFormCheck,
} from '@coreui/react-pro'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  customPageSize,
  initialCandidateInterviewFilterValues,
  initialPage,
  maxExperience,
} from '../CandidateListHelpers'
import {
  GetInterviewCandidateListType,
  InterviewCandidateListConfigType,
} from '../../../../types/Recruitment/CandidateList/CandidateInterviewListTypes'
import { OSelectDynamic } from '../../../../components/ReusableComponent/OSelectList'
import { reduxServices } from '../../../../reducers/reduxServices'
import { useAppDispatch, useTypedSelector } from '../../../../stateStore'
import {
  createNumberArray,
  downloadFile,
  listComposer,
  selectDateOptions,
  StringEmpty,
} from '../../../../utils/helper'
import { OAutoCompleteDynamic } from '../../../../components/ReusableComponent/OAutoComplete'
import ModifiedDatePicker from '../../../../components/ReusableComponent/ModifiedDatePicker'
import {
  DateIsBefore,
  FormatDateHelper,
} from '../../../../utils/dateFormatUtils'
import { openToDateHandler } from '../../../../utils/datePicketUtils'
import { dateFormat } from '../../../../constant/DateFormat'
import { OInputFieldDynamic } from '../../../../components/ReusableComponent/OInputField'
import {
  emptyString,
  returnValidatedDecimal,
} from '../../../../constant/constantData'
import { registerRelatedRoute } from '../../../../utils/paginationUtils'

const CandidateInterviewListFilterOptions = (props: {
  filterBody: GetInterviewCandidateListType
  setFilterBody: React.Dispatch<
    React.SetStateAction<GetInterviewCandidateListType>
  >
  setScreenToggle?: React.Dispatch<React.SetStateAction<string>>
  reloadData: (
    pageSize: number,
    page: number,
    config?: InterviewCandidateListConfigType,
  ) => void
  resetPagination: (identifier?: string) => void
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const history = useHistory()

  //* As you can see when we made the filter data as redux store, we have elimated the prop drilling issue
  //* This is important since we should avoid prop drilling at all cost
  const getFilterData = useTypedSelector(
    reduxServices.candidateInterviewList.selectors
      .getInterviewCandidateListBody,
  )
  const getAllEmpCountries = useTypedSelector(
    reduxServices.candidateList.selectors.getAllEmpCountries,
  )
  const getAllTechnology = useTypedSelector(
    reduxServices.candidateList.selectors.getAllTechnology,
  )
  const getAllStatus = useTypedSelector(
    reduxServices.candidateList.selectors.getAllStatus,
  )
  const allEmployeesList = useTypedSelector(
    (state) => state.candidateList.allEmployeeDetailsList,
  )
  const [dateError, setDateError] = useState<boolean>(false)
  // Track if radio button has been clicked to avoid triggering on initial load
  const [isResumeFilterChanged, setIsResumeFilterChanged] = useState(false)

  const [recruiterType, setRecruiterType] = React.useState<string>('All')
  const [isRecruiterTypeChanged, setIsRecruiterTypeChanged] =
    React.useState(false)
  const isVendor = localStorage.getItem('isVendor') === 'true'

  const vendorsList = useTypedSelector(
    reduxServices.resumeMatcher.selectors.selectVendorsList,
  )

  const sortedTechnologyDetails = useMemo(() => {
    if (getAllTechnology) {
      const sortTemp = getAllTechnology
        ?.slice()
        ?.sort((sortNode1, sortNode2) =>
          sortNode1?.name.localeCompare(sortNode2?.name),
        )
      return listComposer(sortTemp as [], 'id', 'name')
    }
    return []
  }, [getAllTechnology])

  type StatusLike =
    | {
        id?: string | number
        name?: string
        status?: string
        statusName?: string
        statusId?: string | number
      }
    | string

  const sortedStatusDetails = useMemo(() => {
    if (!Array.isArray(getAllStatus) || getAllStatus.length === 0) {
      return [{ id: 'All', name: 'All' }]
    }
    const normalized = (getAllStatus.slice() as StatusLike[])
      .map((s) => {
        if (typeof s === 'object' && s !== null) {
          const name = s.name ?? s.status ?? s.statusName ?? ''
          const id = s.id ?? s.statusId ?? name
          return { id: String(id), name }
        }
        const primitiveName = String(s ?? '')
        return { id: primitiveName, name: primitiveName }
      })
      .filter((x) => x.name !== '')
      .sort((a, b) => a.name.localeCompare(b.name))

    // Apply same display formatting as candidate list: certain statuses use spaces, others use hyphens
    const spaceStatuses = new Set([
      'DID_NOT_JOIN',
      'IN_PROCESS',
      'NO_SHOW',
      'OFFER_CANCELLED',
    ])

    const formatted = normalized.map((x) => {
      const nameStr = String(x.name)
      const displayName = spaceStatuses.has(nameStr)
        ? nameStr.replace(/_/g, ' ')
        : nameStr.replace(/_/g, '-')
      return { id: x.id, name: displayName }
    })

    return [{ id: 'All', name: 'All' }, ...formatted]
  }, [getAllStatus])

  const composedCountries = useMemo(() => {
    return listComposer(getAllEmpCountries as [], 'id', 'name')
  }, [getAllEmpCountries])

  const composedVendors = useMemo(() => {
    if (!Array.isArray(vendorsList)) return []

    return vendorsList.map((vendor, index) => ({ id: index, name: vendor }))
  }, [vendorsList])

  const composedDateOptions = listComposer(
    selectDateOptions as [],
    'label',
    'name',
  )

  const experienceOptions = createNumberArray(maxExperience)

  const filterOnChangeHandler = (
    event:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target
    if (name === 'selectionStatus' && value !== 'Custom') {
      props.setFilterBody((prev) => ({
        ...prev,
        [name]: value,
        fromDate: emptyString,
        toDate: emptyString,
      }))
    } else if (name === 'experience') {
      props.setFilterBody((prev) => ({
        ...prev,
        [name]: returnValidatedDecimal(value, props.filterBody.experience),
      }))
    } else {
      props.setFilterBody((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const recruiterTypeChangeHandler = (
    event:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { value } = event.target
    setRecruiterType(value)
    let isExternalCreatedValue: string | boolean = 'All'
    if (value === 'Internal') {
      isExternalCreatedValue = false
    } else if (value === 'External') {
      isExternalCreatedValue = true
    }
    props.setFilterBody((prev) => ({
      ...prev,
      recruiter: '',
      isExternalCreated: isExternalCreatedValue,
    }))
    setIsRecruiterTypeChanged(true)
  }

  const viewButtonHandler = () => {
    // also the search handler

    //* Don't forget to pass the filter data in this function
    //* since the function still uses the old filter data from redux store, and we need to override it with new filter data
    //* the updated filter body displayed by the UI will be the data to be updated in the redux store
    props.reloadData(customPageSize, initialPage, props.filterBody)
    props.resetPagination()
  }

  const clearButtonHandler = () => {
    //* in clear button handler, we need to reset the filter data in redux store and also the local state
    props.setFilterBody(initialCandidateInterviewFilterValues)
    props.reloadData(
      customPageSize,
      initialPage,
      initialCandidateInterviewFilterValues,
    )
    props.resetPagination()
    setRecruiterType('All')
  }

  //* The export handler should only use the filter data from redux store
  const handleExportCandidateInterview = async () => {
    const payload: InterviewCandidateListConfigType = {
      selectionStatus:
        getFilterData.selectionStatus ||
        initialCandidateInterviewFilterValues.selectionStatus,
      fromDate:
        getFilterData.fromDate ||
        initialCandidateInterviewFilterValues.fromDate,
      toDate:
        getFilterData.toDate || initialCandidateInterviewFilterValues.toDate,
      searchByCandidateName: getFilterData.searchByCandidateName || emptyString,
      searchByRecruiterName: getFilterData.searchByRecruiterName || emptyString,
      searchBySourceName: getFilterData.searchBySourceName || emptyString,
      searchByMultipleFlag: getFilterData.searchByMultipleFlag || emptyString,
      candidateStatus:
        getFilterData.candidateStatus ||
        initialCandidateInterviewFilterValues.candidateStatus,
      selectionTechnology: getFilterData.selectionTechnology || emptyString,
      selectionCountry: getFilterData.selectionCountry || emptyString,
      experience: getFilterData.experience || emptyString,
      interviewRound: getFilterData.interviewRound || emptyString,
      isResumeMatcher: getFilterData.isResumeMatcher || emptyString,
      isExternalCreated: getFilterData.isExternalCreated,
      recruiter: getFilterData.recruiter || emptyString,
    }

    const exportCandidateBlob = await dispatch(
      reduxServices.candidateInterviewList.exportInterviewCandidatesList(
        payload,
      ),
    )
    downloadFile(
      exportCandidateBlob?.payload as Blob,
      'CandidateInterviewList.csv',
    )
  }

  useEffect(() => {
    //INITIATE ALL THE DROPDOWN VALUES FROM API
    dispatch(reduxServices.candidateList.getEmpCountries())
    dispatch(reduxServices.candidateList.getTechnology())
    dispatch(reduxServices.candidateList.getAllEmployeeDetailsThunk())
    dispatch(reduxServices.candidateList.getStatus())
    dispatch(
      reduxServices.vendorList.getVendors({
        startIndex: 0,
        endIndex: 1000,
        vendorName: '',
        isHrVendor: true,
      }),
    )
    dispatch(reduxServices.resumeMatcher.getExternalVendorsList())
  }, [])

  // Effect to sync recruiter type from filterBody
  useEffect(() => {
    const { isExternalCreated } = props.filterBody
    if (isExternalCreated === false) {
      setRecruiterType('Internal')
    } else if (isExternalCreated === true) {
      setRecruiterType('External')
    } else {
      setRecruiterType('All')
    }
  }, [props.filterBody.isExternalCreated])

  // Effect to trigger view when recruiter type changes
  useEffect(() => {
    if (isRecruiterTypeChanged) {
      viewButtonHandler()
      setIsRecruiterTypeChanged(false)
    }
  }, [props.filterBody.isExternalCreated, isRecruiterTypeChanged])

  // Effect to trigger view when resume filter changes
  useEffect(() => {
    if (isResumeFilterChanged) {
      viewButtonHandler()
      setIsResumeFilterChanged(false)
    }
  }, [props.filterBody.isResumeMatcher, isResumeFilterChanged])

  const fromDateChangeHandler = (date: Date) => {
    // converts the dateString into a preferred format 'dd/mm/yyyy'
    const istFormatted = FormatDateHelper(date)
    // assigns the value istFormatted into a state.
    props.setFilterBody((prev) => ({
      ...prev,
      fromDate: istFormatted,
    }))
    // dispatches the value istFormatted to a global state.
  }

  const toDateChangeHandler = (date: Date) => {
    const istFormatted = FormatDateHelper(date)
    props.setFilterBody((prev) => ({
      ...prev,
      toDate: istFormatted,
    })) // dd/mm/yyyy format
  }

  useEffect(() => {
    const isBefore = DateIsBefore(
      props.filterBody.toDate,
      props.filterBody.fromDate,
      dateFormat,
      {
        inclusivity: false,
      },
    )
    setDateError(isBefore)
  }, [props.filterBody.fromDate, props.filterBody.toDate, dateFormat])

  const enableViewButton = useMemo(() => {
    const dateCustomFlag = () => {
      if (props.filterBody.selectionStatus === 'Custom') {
        return (
          StringEmpty(props.filterBody.fromDate) ||
          StringEmpty(props.filterBody.toDate)
        )
      }
      return false
    }

    return dateCustomFlag() || dateError
  }, [dateError, props.filterBody])

  return (
    <>
      <CRow
        xs={{ cols: 1, gutterY: 1 }}
        md={{ cols: 1, gutterY: 4 }}
        xl={{ cols: 2 }}
      >
        <CCol xs={{ span: 12, order: 1 }} xl={{ span: 10, order: 0 }}>
          <CRow sm={{ cols: 1 }} md={{ gutterX: 1 }}>
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="mt-2"
              selectClassName="pe-2"
              name="candidateStatus"
              label="Status"
              ariaLabel="Default select example"
              list={sortedStatusDetails.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
              value={props.filterBody.candidateStatus}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Status'}
            />
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="mt-2"
              selectClassName="pe-2"
              name="selectionTechnology"
              label="Technology"
              ariaLabel="Default select example"
              list={sortedTechnologyDetails}
              value={props.filterBody.selectionTechnology}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Technology'}
            />
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="mt-2"
              selectClassName="pe-2"
              name="selectionCountry"
              label="Country"
              ariaLabel="Default select example"
              list={composedCountries}
              useIdValueOptions={true}
              value={String(props.filterBody.selectionCountry)}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Country'}
            />
          </CRow>
          <CRow
            className="mb-2 mt-md-1"
            sm={{ cols: 1 }}
            lg={{ cols: 3, gutterY: 3, gutterX: 1 }}
          >
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="mt-2"
              selectClassName="pe-2"
              name="interviewRound"
              label="Interview Round"
              ariaLabel="Default select example"
              list={experienceOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
              value={props.filterBody.interviewRound as string}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Round'}
            />
            <OInputFieldDynamic
              inputMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              inputColumnType={{ sm: 8 }}
              labelClassName="mt-2"
              inputClassName="pe-2"
              name="experience"
              label="Experience"
              value={props.filterBody.experience}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Experience'}
              autoComplete="off"
            />
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="mt-2"
              selectClassName="pe-2"
              name="selectionStatus"
              label="Date"
              ariaLabel="Default select example"
              list={composedDateOptions}
              value={props.filterBody.selectionStatus}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Date'}
            />
          </CRow>
          {!isVendor && (
            <CRow
              className="mb-2 mt-md-1"
              sm={{ cols: 1 }}
              lg={{ cols: 3, gutterY: 3, gutterX: 1 }}
            >
              <OSelectDynamic
                selectMainColType={{ md: 4 }}
                labelColumnType={{ sm: 4 }}
                selectColumnType={{ sm: 8 }}
                labelClassName="mt-2"
                selectClassName="pe-2"
                name="recruiterType"
                label="Recruiter"
                ariaLabel="Select recruiter type"
                list={{
                  Internal: 'Internal',
                  External: 'External',
                }}
                value={recruiterType || 'Select recruiter'}
                setValueHandler={recruiterTypeChangeHandler}
                placeHolder="Select recruiter"
              />

              {recruiterType === 'Internal' && (
                <CCol md={4}>
                  <OAutoCompleteDynamic
                    labelColumnType={{ sm: 4 }}
                    selectColumnType={{ sm: 8 }}
                    items={allEmployeesList}
                    autocomplete="on"
                    placeholder="Recruiter Name"
                    label="Recruiter Name"
                    name="recruiter"
                    value={props.filterBody.recruiter}
                    setValueHandler={filterOnChangeHandler}
                    onSelect={(selectedVal) => {
                      props.setFilterBody((prev) => ({
                        ...prev,
                        recruiter: selectedVal,
                      }))
                    }}
                  />
                </CCol>
              )}
              {recruiterType === 'External' && (
                <OSelectDynamic
                  selectMainColType={{ md: 4 }}
                  labelColumnType={{ sm: 4 }}
                  selectColumnType={{ sm: 8 }}
                  labelClassName="mt-2"
                  selectClassName="pe-2"
                  name="recruiter"
                  label="Vendor Name"
                  ariaLabel="Select vendor"
                  list={composedVendors}
                  value={props.filterBody.recruiter}
                  setValueHandler={filterOnChangeHandler}
                  placeHolder="Select Vendor"
                />
              )}
            </CRow>
          )}
          <CRow className="mb-3 mt-3">
            <CCol xs={12}>
              <div className="d-inline ms-4">
                <div className="d-inline">
                  <CFormCheck
                    type="radio"
                    name="resumeFilter"
                    id="allCandidates"
                    label="All"
                    value="all"
                    checked={
                      !props.filterBody.isResumeMatcher ||
                      props.filterBody.isResumeMatcher === ''
                    }
                    onChange={() => {
                      props.setFilterBody((prev) => ({
                        ...prev,
                        isResumeMatcher: '',
                      }))
                      setIsResumeFilterChanged(true)
                    }}
                    inline
                  />
                  <CFormCheck
                    type="radio"
                    name="resumeFilter"
                    id="resumeMatcher"
                    label="Resume Matcher"
                    value="matcher"
                    checked={props.filterBody.isResumeMatcher === 'true'}
                    onChange={() => {
                      props.setFilterBody((prev) => ({
                        ...prev,
                        isResumeMatcher: 'true',
                      }))
                      setIsResumeFilterChanged(true)
                    }}
                    inline
                  />
                  <CFormCheck
                    type="radio"
                    name="resumeFilter"
                    id="resumeParser"
                    label="Resume Parser"
                    value="parser"
                    checked={props.filterBody.isResumeMatcher === 'false'}
                    onChange={() => {
                      props.setFilterBody((prev) => ({
                        ...prev,
                        isResumeMatcher: 'false',
                      }))
                      setIsResumeFilterChanged(true)
                    }}
                    inline
                  />
                </div>
              </div>
            </CCol>
          </CRow>
          {props.filterBody.selectionStatus === 'Custom' ? (
            <>
              <CRow className="mb-4 mt-3" xs={{ cols: 1 }} md={{ gutter: 1 }}>
                <CCol md={4}>
                  <CRow>
                    <CCol sm={4} className="text-md-end">
                      <CFormLabel className="mt-2">
                        From :
                        {StringEmpty(props.filterBody.fromDate) && (
                          <span className="text-danger">*</span>
                        )}
                      </CFormLabel>
                    </CCol>
                    <CCol sm={8}>
                      <ModifiedDatePicker
                        id="fromDate"
                        data-testid="candidateInterviewFromDate"
                        className="form-control form-control-sm sh-date-picker"
                        autoComplete="off"
                        dropdownMode="select"
                        showMonthDropdown
                        showYearDropdown
                        onChange={fromDateChangeHandler}
                        value={props.filterBody.fromDate}
                        openToDate={openToDateHandler(
                          props.filterBody.fromDate,
                        )}
                        disabledKeyboardNavigation={true}
                        selected={props.filterBody.fromDate}
                      />
                    </CCol>
                  </CRow>
                </CCol>
                <CCol md={4}>
                  <CRow>
                    <CCol sm={4} className="text-md-end">
                      <CFormLabel className="mt-2">
                        To :
                        {StringEmpty(props.filterBody.toDate) && (
                          <span className="text-danger">*</span>
                        )}
                      </CFormLabel>
                    </CCol>
                    <CCol sm={8}>
                      <ModifiedDatePicker
                        id="toDate"
                        data-testid="ticketReportFromDate"
                        className="form-control form-control-sm sh-date-picker"
                        dropdownMode="select"
                        showMonthDropdown
                        autoComplete="off"
                        showYearDropdown
                        onChange={toDateChangeHandler}
                        value={props.filterBody.toDate}
                        openToDate={openToDateHandler(props.filterBody.toDate)}
                        disabledKeyboardNavigation={true}
                        selected={props.filterBody.toDate}
                      />
                    </CCol>
                  </CRow>
                </CCol>
                <CCol md={4}>
                  {dateError && (
                    <CRow className="ps-md-3">
                      <CCol md={10} xl={7}>
                        <span className="text-danger">
                          <b>To date should be greater than From date</b>
                        </span>
                      </CCol>
                    </CRow>
                  )}
                </CCol>
              </CRow>
            </>
          ) : (
            <></>
          )}
        </CCol>
        <CCol
          className="text-end"
          xs={{ span: 12, order: 0 }}
          sm={{ span: 12, order: 0 }}
          md={{ span: 12, order: 0 }}
          xl={{ span: 2, order: 1 }}
        >
          <CButton
            color="primary"
            data-testid="back-btn"
            className="btn-ovh me-0 mt-md-0 mt-1"
            onClick={() => {
              dispatch(registerRelatedRoute())
              history.goBack()
            }}
          >
            <i className="fa fa-arrow-left me-1"></i>Back
          </CButton>
        </CCol>
      </CRow>
      <CRow
        className="justify-content-center mt-4 mt-sm-5 pt-2 pe-sm-5 me-sm-5"
        xs={{ cols: 2 }}
      >
        <CCol sm={'auto'} className="text-right">
          <CButton
            className="cursor-pointer"
            color="success btn-ovh"
            onClick={(e) => {
              viewButtonHandler()
              ;(e.target as HTMLButtonElement).blur()
            }}
            data-testId="view-btn"
            disabled={enableViewButton}
          >
            View
          </CButton>
        </CCol>
        <CCol sm={'auto'}>
          <CButton
            className="cursor-pointer"
            disabled={false}
            data-testid="clear-btn"
            color="warning btn-ovh"
            onClick={(e) => {
              clearButtonHandler()
              ;(e.target as HTMLButtonElement).blur()
            }}
          >
            Clear
          </CButton>
        </CCol>
      </CRow>
      <CRow className="justify-content-end mt-4">
        <CCol className="text-end mb-1" md={'auto'} sm={'auto'} xs={5}>
          <CButton
            color="primary"
            className="btn-ovh me-0 mt-md-0 mt-1"
            size="sm"
            onClick={handleExportCandidateInterview}
            data-testId="export-Candidate-Interview"
          >
            <i className="fa fa-plus me-1"></i>
            Click to Export
          </CButton>
        </CCol>
      </CRow>
      <CRow className="justify-content-end mt-3">
        <CCol className="text-end" md={'auto'} sm={'auto'} xs={9}>
          <span className="text-danger" data-testid="errorMessage">
            <b> Note: Select a date to proceed with exporting the data.</b>
          </span>
        </CCol>
      </CRow>
      <CRow className="gap-2 d-md-flex justify-content-md-end mt-3 mb-3">
        <CCol sm={6} md={4} lg={5} xl={4} xxl={3}>
          <CInputGroup className="global-search me-0">
            <CFormInput
              id="searchByMultipleFlag"
              name="searchByMultipleFlag"
              data-testid="searchField"
              placeholder="Multiple Search"
              aria-label="Multiple Search"
              aria-describedby="button-addon2"
              value={props.filterBody.searchByMultipleFlag?.replace(
                /^\s*/,
                emptyString,
              )}
              onChange={filterOnChangeHandler}
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  props.resetPagination('search-reset')
                  props.reloadData(customPageSize, initialPage, {
                    searchByMultipleFlag:
                      props.filterBody.searchByMultipleFlag.replace(
                        /\t/g,
                        emptyString,
                      ),
                  })
                }
              }}
            />
            <CButton
              disabled={StringEmpty(props.filterBody.searchByMultipleFlag)}
              data-testid="multi-search-btn"
              className="cursor-pointer"
              type="button"
              color="primary"
              id="button-addon2"
              onClick={() => {
                props.resetPagination('search-reset')
                props.reloadData(customPageSize, initialPage, {
                  searchByMultipleFlag:
                    props.filterBody.searchByMultipleFlag.replace(
                      /\t/g,
                      emptyString,
                    ),
                })
              }}
            >
              <i className="fa fa-search"></i>
            </CButton>
          </CInputGroup>
        </CCol>
      </CRow>
    </>
  )
}

export default CandidateInterviewListFilterOptions
